import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export type ConflictStatus = "open" | "resolved";

interface Party {
  id: string;
  name: string;
  initials: string;
  role: string;
  org: "client" | "nymbl";
}

export interface ConflictPosition {
  id: string;
  stance: string;
  party: Party | null;
}

export interface ConflictSource {
  id: string;
  name: string;
  type: string;
}

export interface ConflictRow {
  id: string;
  solutionId: string;
  externalKey: string | null;
  description: string;
  status: ConflictStatus;
  conflictDate: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  positions: ConflictPosition[];
  relatedItemIds: string[];
}

export interface ConflictDetail extends ConflictRow {
  sources: ConflictSource[];
}

export interface ConflictPatchBody {
  status?: ConflictStatus;
  resolutionNote?: string | null;
}

const conflictKeys = {
  list: (solutionId: string) => ["conflicts", "list", solutionId] as const,
  detail: (id: string) => ["conflicts", "detail", id] as const,
} as const;

export function useConflicts(solutionId: string) {
  return useQuery({
    queryKey: conflictKeys.list(solutionId),
    queryFn: async (): Promise<ConflictRow[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/conflicts`);
      if (!res.ok) throw new Error("Failed to fetch conflicts");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export function useConflict(conflictId: string | null) {
  return useQuery({
    queryKey: conflictKeys.detail(conflictId ?? ""),
    queryFn: async (): Promise<ConflictDetail> => {
      const res = await fetch(`/api/conflicts/${conflictId}`);
      if (!res.ok) throw new Error("Failed to fetch conflict");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(conflictId),
  });
}

export function usePatchConflict(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: ConflictPatchBody }) => {
      const res = await fetch(`/api/conflicts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("Failed to update conflict");
      const json = await res.json();
      return json.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: conflictKeys.list(solutionId) });
      qc.invalidateQueries({ queryKey: conflictKeys.detail(vars.id) });
    },
    onError: () => toast.error("Failed to update conflict"),
  });
}
